// Battery / charging bridge. Uses @capacitor/device on native iOS/Android.
//
// On web we deliberately do NOT call navigator.getBattery():
//  - iOS Safari doesn't implement it.
//  - Chrome returns the laptop battery, not the phone.
// Both lead to a misleading "Right now" pill, so we return nulls and let the
// UI hide itself (and the upload gate treat unknown as pass — Wi-Fi check is enough).
import { Device } from "@capacitor/device";
import { isNative } from "./platform";

export type BatteryInfo = {
  /** 0..1, or null if unknown */
  level: number | null;
  /** true if plugged in / charging, or null if unknown */
  isCharging: boolean | null;
};

function sanitize(raw: { batteryLevel?: number; isCharging?: boolean }): BatteryInfo {
  const lvl = typeof raw.batteryLevel === "number" ? raw.batteryLevel : NaN;
  // iOS UIDevice returns -1 (or values outside [0,1]) until battery monitoring
  // is enabled and a value has been published. Treat those as "unknown" rather
  // than rendering a fake number.
  const level = Number.isFinite(lvl) && lvl >= 0 && lvl <= 1 ? lvl : null;
  const isCharging = typeof raw.isCharging === "boolean" ? raw.isCharging : null;
  return { level, isCharging };
}

export async function getBatteryInfo(): Promise<BatteryInfo> {
  if (!isNative()) {
    return { level: null, isCharging: null };
  }
  try {
    const first = sanitize(await Device.getBatteryInfo());
    // A first-read value of exactly 1.0 on iOS is suspicious — it's the
    // value the plugin returns before iOS publishes the real level (the
    // race where UIDevice.batteryLevel went from -1 to 1.0 default).
    // Real fully-charged devices stay at 1.0 on the second read too, so
    // we retry once and accept whatever comes back.
    const suspicious = first.level === null || first.level === 1;
    if (!suspicious) return first;
    await new Promise((r) => setTimeout(r, 250));
    const second = sanitize(await Device.getBatteryInfo());
    return {
      level: second.level ?? first.level,
      isCharging: second.isCharging ?? first.isCharging,
    };
  } catch {
    return { level: null, isCharging: null };
  }
}

/**
 * Decide whether the current battery state allows an auto-upload.
 * - If level is unknown → pass (don't block on unknown — Wi-Fi check is enough).
 * - If charging → always pass when `requireChargingUnderMin` is on (handles slow solar).
 * - Otherwise require level*100 >= minPct.
 */
export function batteryAllowsUpload(
  info: BatteryInfo,
  minPct: number,
  requireChargingUnderMin: boolean,
): { ok: boolean; reason?: string } {
  if (info.level === null) return { ok: true };
  const pct = Math.round(info.level * 100);
  if (pct >= minPct) return { ok: true };
  if (requireChargingUnderMin && info.isCharging === true) return { ok: true };
  return { ok: false, reason: `Battery ${pct}% < ${minPct}% and not charging.` };
}

/**
 * Read current battery state and apply the user's upload rule.
 * Returns `ok: true` on web (level unknown — Wi-Fi/cellular gate is enough)
 * and on native when the rule passes. On native failure, the caller gets
 * back the actual percentage + charging state so it can render a useful
 * confirm dialog ("Battery 12% — your minimum is 20%. Upload anyway?").
 */
export async function evaluateBatteryGate(
  minPct: number,
  requireChargingUnderMin: boolean,
): Promise<
  | { ok: true; level: number | null; isCharging: boolean | null; minPct: number }
  | { ok: false; level: number; isCharging: boolean | null; minPct: number; reason: string }
> {
  const info = await getBatteryInfo();
  const verdict = batteryAllowsUpload(info, minPct, requireChargingUnderMin);
  if (verdict.ok) {
    return { ok: true, level: info.level, isCharging: info.isCharging, minPct };
  }
  // batteryAllowsUpload only returns ok:false when info.level is a number.
  return {
    ok: false,
    level: info.level as number,
    isCharging: info.isCharging,
    minPct,
    reason: verdict.reason ?? "Battery too low.",
  };
}

